import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const API_BASE       = process.env.REACT_APP_API_URL || 'https://api.resources.aurx.co/api';
const ZOHO_LOGIN_URL = `${API_BASE}/auth/sso/login`;   // SAML SSO — redirects to Zoho IdP

// Loop-breaker: count SSO redirect attempts. If we exceed the threshold
// (e.g. /login → SSO → /dashboard → bounced back to /login repeatedly), stop
// auto-redirecting and let the user click the manual button.
//
// MUST be localStorage, NOT sessionStorage: the SSO round trip navigates
// cross-origin (app → api → Zoho → api → app). On mobile browsers and in-app
// webviews, sessionStorage is frequently reset across that round trip, so a
// sessionStorage counter never accumulates and the guard never fires — which is
// exactly why the loop ran forever on mobile but self-stopped on desktop.
// localStorage survives the round trip on every platform.
//
// We count back-to-back attempts and reset only when the most recent attempt
// was long ago (a genuinely new sign-in) — a fixed time window was too short for
// slow mobile round trips, so attempts aged out before the limit was reached.
// AuthContext.loadUser() also clears SSO_LOOP_KEY on a successful /auth/me, so
// the counter only ever reflects CONSECUTIVE attempts that never reached an
// authenticated session. Keep the key name in sync with AuthContext.
const SSO_LOOP_KEY          = 'ssoLoopGuard';   // JSON array of attempt timestamps
const SSO_LOOP_RESET_GAP_MS = 10 * 60_000;      // gap that marks a fresh sign-in
const SSO_LOOP_MAX_ATTEMPTS = 3;

// Zoho logo SVG
function ZohoLogo({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <rect width="40" height="40" rx="6" fill="#E42527"/>
      <text x="50%" y="55%" dominantBaseline="middle" textAnchor="middle"
        fill="white" fontSize="18" fontWeight="bold" fontFamily="Arial, sans-serif">
        Z
      </text>
    </svg>
  );
}

export default function Login() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const hasRun = useRef(false);

  useEffect(() => {
    // Run once per mount. React 18 StrictMode (active in dev builds) double-invokes
    // effects; without this guard the loop counter would be incremented twice and
    // the Zoho redirect fired twice on every visit. No-op in a production build.
    if (hasRun.current) return;
    hasRun.current = true;

    const err = searchParams.get('error');
    if (err === 'session_expired') {
      setError('Your account is disabled or your session is no longer valid. Please contact your administrator.');
      return;
    }
    if (err) {
      setError('Sign-in failed. Please try again or contact your administrator.');
      return;
    }

    // Loop guard: read prior attempts. If the most recent attempt was long ago,
    // this is a fresh sign-in — start over. Otherwise the attempts are back-to-back
    // (a redirect loop), so count them and stop once we hit the limit instead of
    // bouncing to Zoho forever.
    let attempts = [];
    try { attempts = JSON.parse(localStorage.getItem(SSO_LOOP_KEY) || '[]'); } catch {}
    const now = Date.now();
    const lastAttempt = attempts.length ? Math.max(...attempts) : 0;
    if (now - lastAttempt > SSO_LOOP_RESET_GAP_MS) attempts = [];

    if (attempts.length >= SSO_LOOP_MAX_ATTEMPTS) {
      localStorage.removeItem(SSO_LOOP_KEY);
      setError(
        `Single sign-on did not complete after ${SSO_LOOP_MAX_ATTEMPTS} attempts. ` +
        'Please click "Sign in with Zoho" below to retry, or contact your administrator.'
      );
      return;
    }

    attempts.push(now);
    localStorage.setItem(SSO_LOOP_KEY, JSON.stringify(attempts));
    window.location.href = ZOHO_LOGIN_URL;
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-red-600/10 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* App logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-600 rounded-2xl mb-4 shadow-lg shadow-indigo-500/30">
            <span className="text-white text-2xl font-bold">R</span>
          </div>
          <h1 className="text-3xl font-bold text-white">RecruitPro</h1>
          <p className="text-slate-400 mt-2">Sign in with your Zoho account</p>
        </div>

        {/* Card */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl">

          {/* Error banner */}
          {error && (
            <div className="mb-6 flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-xl p-4">
              <svg className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          {/* Zoho icon */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
              <svg width="36" height="36" viewBox="0 0 40 40" fill="none">
                <rect width="40" height="40" rx="8" fill="#E42527"/>
                <text x="50%" y="54%" dominantBaseline="middle" textAnchor="middle"
                  fill="white" fontSize="22" fontWeight="bold" fontFamily="Arial, sans-serif">
                  Z
                </text>
              </svg>
            </div>
          </div>

          <h2 className="text-center text-lg font-semibold text-white mb-1">
            Welcome to RecruitPro
          </h2>
          <p className="text-center text-sm text-slate-400 mb-8">
            Use your Zoho organisation account to sign in.
            <br />You'll be redirected to Zoho to authenticate.
          </p>

          {/* Zoho Sign-in Button */}
          <a
            href={ZOHO_LOGIN_URL}
            style={{
              display:         'flex',
              alignItems:      'center',
              justifyContent:  'center',
              gap:             12,
              width:           '100%',
              backgroundColor: '#E42527',
              color:           '#ffffff',
              border:          'none',
              borderRadius:    6,
              padding:         '11px 20px',
              fontSize:        15,
              fontWeight:      600,
              fontFamily:      '"Segoe UI", system-ui, sans-serif',
              textDecoration:  'none',
              transition:      'background-color 0.15s, box-shadow 0.15s',
              letterSpacing:   '0.01em',
              cursor:          'pointer',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#c41f21';
              e.currentTarget.style.boxShadow = '0 4px 14px rgba(228,37,39,0.35)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = '#E42527';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <ZohoLogo size={20} />
            Sign in with Zoho
          </a>

          {/* Footer */}
          <p className="text-center text-xs text-slate-500 mt-6">
            Access is managed by your Zoho organisation administrator.
            <br />Contact them if you're having trouble signing in.
          </p>
        </div>
      </div>
    </div>
  );
}
